import lodash from 'lodash';

import type { Environment, RicosTheme } from './../ricos-types/index.js';

import {
  HEADING_FONT_SIZES,
  PARAGRAPH_FONT_SIZE,
  ACTION_COLOR_DEFAULT,
  BG_COLOR_DEFAULT,
  TEXT_COLOR_DEFAULT,
} from './theme-constants.js';

type ResolveThemeOptions = {
  theme?: RicosTheme;
  environment?: Environment;
  isMobile?: boolean;
};

/**
 * Single theme resolution function.
 *
 * Merge order: baseDefaults <- envPreset <- userTheme <- priorityOverrides
 *
 * - baseDefaults: device-specific defaults (desktop/mobile font sizes, palette)
 * - envPreset: environment-specific preset (wix/liveSite/native)
 * - userTheme: user-provided theme overrides
 * - priorityOverrides: forced overrides that cannot be changed by the user (wix-only)
 */
export const resolveTheme = ({
  theme = {},
  environment,
  isMobile = false,
}: ResolveThemeOptions): RicosTheme => {
  const { useWixStyles, useNativeStyles, ...userTheme } = theme;

  // 1. Determine environment
  const isWix = useWixStyles || environment === 'businessManager' || environment === 'wixel';
  const envPreset = useNativeStyles ? nativePreset : isWix ? wixPreset : liveSitePreset;

  // 2. Pick device-specific base defaults
  const baseDefaults = isMobile ? THEME_DEFAULTS.mobile : THEME_DEFAULTS.desktop;

  // 3. Merge in clear, documented order:
  //    base defaults <- environment preset <- user overrides <- priority overrides
  return lodash.merge({}, baseDefaults, envPreset, userTheme, isWix ? wixPriorityOverrides : {});
};

// ── Environment Presets ──────────────────────────────────────────────────────
// Moved from wix-ricos-common/themeStrategy/useWixStyle.ts, useLiveSiteStyle.ts, useNativeStyle.ts

const wixPreset: RicosTheme = {
  palette: {
    actionColor: ACTION_COLOR_DEFAULT,
    bgColor: BG_COLOR_DEFAULT,
    textColor: TEXT_COLOR_DEFAULT,
  },
  paletteConfig: {
    focusActionColor: ACTION_COLOR_DEFAULT,
    settingsActionColor: ACTION_COLOR_DEFAULT,
  },
  settingsStyles: {
    dividers: {
      color: '#DFE5EB',
      height: '12px',
    },
    icons: {
      color: '#333853',
    },
    text: {
      color: '#333853',
      fontFamily: 'Madefor',
    },
    whitebox: {
      borderRadius: '8px',
      boxShadow: '0 8px 8px 0 rgba(22, 45, 61, 0.12), 0 3px 24px 0 rgba(22, 45, 61, 0.18)',
    },
  },
};

// These theme styles should always override provided theme styles in wix environment
const wixPriorityOverrides: RicosTheme = {
  customStyles: {
    editor: {
      addPluginButton: {
        plusIconColor: ACTION_COLOR_DEFAULT,
      },
    },
  },
};

const liveSitePreset: RicosTheme = {
  palette: {},
  paletteConfig: {
    settingsActionColor: '#000000',
  },
  settingsStyles: {
    dividers: {
      color: '#ededed',
      height: '12px',
    },
    icons: {
      color: '#000000',
    },
    text: {
      color: '#000000',
      fontFamily: 'HelveticaNeue,Helvetica,Arial',
    },
    whitebox: {
      borderRadius: '0px',
      boxShadow: '0 8px 8px 0 rgba(22, 45, 61, 0.12), 0 3px 24px 0 rgba(22, 45, 61, 0.18)',
    },
  },
};

const nativePreset: RicosTheme = {
  nativeStyle: {
    text: {
      fontFamily: 'Madefor',
      color: '#20303C',
    },
    primaryText: {
      fontWeight: 700,
    },
    bgColor: {
      backgroundColor: '#F8F9FA',
    },
    secondary: {
      backgroundColor: '#E8ECF0',
    },
    whitebox: {
      borderRadius: '8px',
    },
    coverImage: {
      height: '70px',
      width: '70px',
      margin: '8px',
      border: '1px solid rgba(110, 120, 129, 0.1)',
    },
  },
};

// reference for values is in consts.scss in common package
const defaultCustomStyle = { fontWeight: 'normal', fontStyle: 'normal', lineHeight: '1.5' };
const defaultPalette = { bgColor: BG_COLOR_DEFAULT, textColor: TEXT_COLOR_DEFAULT };

const THEME_DEFAULTS: {
  desktop: RicosTheme;
  mobile: RicosTheme;
} = {
  desktop: {
    customStyles: {
      p: {
        ...defaultCustomStyle,
        fontSize: PARAGRAPH_FONT_SIZE,
      },
      h1: {
        ...defaultCustomStyle,
        fontSize: HEADING_FONT_SIZES.h1,
      },
      h2: {
        ...defaultCustomStyle,
        fontSize: HEADING_FONT_SIZES.h2,
      },
      h3: {
        ...defaultCustomStyle,
        fontSize: HEADING_FONT_SIZES.h3,
      },
      h4: {
        ...defaultCustomStyle,
        fontSize: HEADING_FONT_SIZES.h4,
      },
      h5: {
        ...defaultCustomStyle,
        fontSize: HEADING_FONT_SIZES.h5,
      },
      h6: {
        ...defaultCustomStyle,
        fontSize: HEADING_FONT_SIZES.h6,
      },
      quote: {
        lineHeight: '1.5',
        fontSize: '18px',
      },
      codeBlock: {
        lineHeight: '1.5',
      },
    },
    palette: defaultPalette,
  },
  mobile: {
    customStyles: {
      p: {
        ...defaultCustomStyle,
        fontSize: '14px',
      },
      h1: {
        ...defaultCustomStyle,
        fontSize: '32px',
      },
      h2: {
        ...defaultCustomStyle,
        fontSize: '24px',
      },
      h3: {
        ...defaultCustomStyle,
        fontSize: '20px',
      },
      h4: {
        ...defaultCustomStyle,
        fontSize: '20px',
      },
      h5: {
        ...defaultCustomStyle,
        fontSize: '16px',
      },
      h6: {
        ...defaultCustomStyle,
        fontSize: '14px',
      },
      quote: {
        lineHeight: '1.5',
        fontSize: '18px',
      },
      codeBlock: {
        lineHeight: '1.5',
      },
    },
    palette: defaultPalette,
  },
};
